import { Linkedin } from 'lucide-react';

interface TeamMember {
  name: string;
  role?: string;
  photoUrl?: string;
  linkedinUrl?: string;
  highlight?: boolean;
}

interface TeamLevel {
  title?: string;
  members?: TeamMember[];
}

interface TeamOrgBlockData {
  title?: string;
  description?: string;
  levels?: TeamLevel[];
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function MemberCard({ member, large }: { member: TeamMember; large?: boolean }) {
  const photoSize = large ? 'h-28 w-28' : 'h-20 w-20';
  const textSize = large ? 'text-sm' : 'text-xs';

  return (
    <div className="flex flex-col items-center gap-2 group">
      {/* Photo */}
      <div className={`relative ${photoSize} flex-shrink-0`}>
        {member.photoUrl ? (
          <img
            src={member.photoUrl}
            alt={member.name}
            className={`${photoSize} rounded-full object-cover ring-2 ring-white shadow-md`}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
              const next = e.currentTarget.nextElementSibling as HTMLElement | null;
              if (next) next.style.display = 'flex';
            }}
          />
        ) : null}
        {/* Fallback initiales — toujours présent, masqué si photo */}
        <div
          className={`${photoSize} rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md ${member.photoUrl ? 'hidden' : 'flex'}`}
        >
          <span className="text-white font-bold text-lg select-none">{initials(member.name)}</span>
        </div>

        {/* LinkedIn badge */}
        {member.linkedinUrl && (
          <a
            href={member.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#0077b5] flex items-center justify-center shadow ring-2 ring-white hover:scale-110 transition-transform"
            aria-label={`LinkedIn de ${member.name}`}
          >
            <Linkedin className="h-3 w-3 text-white" />
          </a>
        )}
      </div>

      {/* Texte */}
      <div className="text-center max-w-[140px]">
        <p className={`font-bold uppercase tracking-wide leading-tight text-neutral-900 ${textSize}`}>
          {member.name}
        </p>
        {member.role && (
          <p className={`uppercase tracking-wide text-neutral-500 mt-0.5 leading-tight ${large ? 'text-xs' : 'text-[10px]'}`}>
            {member.role}
          </p>
        )}
      </div>
    </div>
  );
}

export default function TeamOrgBlock({ block }: { block: TeamOrgBlockData & { type: string } }) {
  const levels = Array.isArray(block.levels) ? block.levels : [];
  const validLevels = levels.filter(
    (l) => Array.isArray(l.members) && l.members.length > 0
  );

  if (validLevels.length === 0) return null;

  return (
    <div className="rounded-2xl bg-white border border-neutral-100 shadow-sm px-6 py-8 md:px-10 md:py-10">
      {/* Header */}
      {block.title && (
        <h2 className="text-xl font-bold text-neutral-900 mb-2 text-center">{block.title}</h2>
      )}
      {block.description && (
        <p className="text-sm text-neutral-500 text-center mb-8 max-w-xl mx-auto">{block.description}</p>
      )}
      {!block.description && block.title && <div className="mb-8" />}

      <div className="space-y-0">
        {validLevels.map((level, levelIdx) => {
          const isFirst = levelIdx === 0;
          const isSecond = levelIdx === 1;
          const members = level.members ?? [];
          const large = isFirst;

          return (
            <div key={levelIdx}>
              {/* Level label */}
              {level.title && (
                <p className="text-center text-[10px] font-semibold uppercase tracking-widest text-neutral-400 mb-4">
                  {level.title}
                </p>
              )}

              {/* Members layout */}
              {(isFirst || isSecond) ? (
                // Level 1 & 2 : centered flex
                <div className="flex flex-wrap justify-center gap-8">
                  {members.map((member, mIdx) => (
                    <MemberCard key={mIdx} member={member} large={large && member.highlight !== false} />
                  ))}
                </div>
              ) : (
                // Level 3+ : responsive grid
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 justify-items-center">
                  {members.map((member, mIdx) => (
                    <MemberCard key={mIdx} member={member} large={false} />
                  ))}
                </div>
              )}

              {/* Separator */}
              {levelIdx < validLevels.length - 1 && (
                <div className="my-8 flex items-center gap-4">
                  <div className={`flex-1 border-t ${isFirst ? 'border-neutral-200' : 'border-dashed border-neutral-200'}`} />
                  <div className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254]" />
                  <div className={`flex-1 border-t ${isFirst ? 'border-neutral-200' : 'border-dashed border-neutral-200'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
